import Ajv from "ajv";
import { CaseModel, schemas } from "data-dictionary";
import deepmerge from "deepmerge";
import { utils } from "xlsx";

const hydroSchema = schemas.find((v) => v.$id === "generator_hydro.schema.json").properties;
const renewableSchema = schemas.find((v) => v.$id === "generator_renewable.schema.json").properties;
const thermalSchema = {
  ...schemas.find((v) => v.$id === "generator_thermal.schema.json").properties,
  ...schemas
    .find((v) => v.$id === "generator_thermal.schema.json")
    .allOf.map((v) => v.then.properties)
    .reduce((obj, item) => ({ ...obj, ...item }), {}),
};
const generatorGenericSchema = deepmerge(deepmerge(hydroSchema, renewableSchema), thermalSchema);

const headerToAttribute = (headerName) => {
  const header = headerName
    .slice(0, headerName.indexOf("[") > -1 ? headerName.indexOf("[") : headerName.length)
    .trim();

  if (
    [
      "Primary Reserve Participation",
      "Secondary Reserve Participation",
      "Tertiary Reserve Participation",
    ].includes(header)
  ) {
    return header;
  }

  const attribute = Object.entries(generatorGenericSchema).find(
    ([_header, v]) => v.title === header
  );
  if (!attribute) {
    throw new Error(`Wrong header. Attribute '${header}' does not exist`);
  }
  return attribute[0];
};

const attributeToHeader = (attribute) => {
  return generatorGenericSchema[attribute].title;
};

const parseError = (error) => {
  const { keyword, dataPath, message, params } = error;
  if (keyword === "enum") {
    const attribute = dataPath.slice(dataPath.lastIndexOf("/") + 1);
    return `Attribute "${attributeToHeader(attribute)}" ${message}: ${params.allowedValues.join(
      ", "
    )}`;
  }
  return `${dataPath.substring(1).replace(/\//g, " > ").replace(/_/g, " ")}: ${message}`;
};

const parseGenerators = (workbook, settings) => {
  const { startYear, numYears } = settings;
  const ajv = new Ajv({
    schemas,
    allErrors: true,
    coerceTypes: true,
    jsonPointers: true,
  });

  const validate = ajv.getSchema("input.schema.json");

  const generators = [];
  // Constants data
  const rows = utils.sheet_to_json(workbook.Sheets["Generators Atemporal Variables"]);
  rows.forEach((row) => {
    const item = {
      ...Object.entries(row).reduce(
        (acc, [key, value]) => ({ ...acc, [headerToAttribute(key)]: value }),
        {}
      ),
      reserves: [
        row["Primary Reserve Participation"] || 0,
        row["Secondary Reserve Participation"] || 0,
        row["Tertiary Reserve Participation"] || 0,
      ],
      ...(row["Coordinates [lat, lng]"]
        ? { coordinates: JSON.parse(`[${row["Coordinates [lat, lng]"]}]`) }
        : {}),
    };
    const generatorItem = new CaseModel.Generator(item);
    // Skip id, profile
    const valid = validate({ generators: [{ ...generatorItem, id: 1, profile: [0] }] });
    if (!valid) {
      throw new Error(parseError(validate.errors[0]));
    }
    generators.push(item);
  });

  // Yearly data
  const yearlyRows = utils.sheet_to_json(workbook.Sheets["Generators Temporal Variables"]);
  const yearsArray = Array(numYears)
    .fill(0)
    .map((_v, i) => startYear + i);
  yearlyRows.forEach((row) => {
    const generator = generators.find((v) => v.name === row.Name);
    const attribute = headerToAttribute(row.Attribute);
    if (generator) {
      const { default: defaultValue } = generatorGenericSchema[attribute].items;
      const values = Array(numYears).fill(defaultValue);
      Object.entries(row)
        .filter(([key]) => !["Name", "Attribute"].includes(key))
        .forEach(([year, value]) => {
          const index = yearsArray.findIndex((v) => v === Number(year));
          if (index > -1) {
            values.splice(index, 1, value);
          }
        });
      generator[attribute] = values;
    }
  });

  return generators;
};

const generatorsTemplate = (settings) => {
  const { startYear, numYears } = settings;
  // init workbook
  const workbook = utils.book_new();
  // Atemporal variables
  const variables = Object.values(generatorGenericSchema).filter(
    (value) =>
      (!value.yearly && value.type !== "array" && value.title) || value.title === "Coordinates"
  );
  variables.unshift(
    variables.splice(
      variables.findIndex((v) => v.title === "Name"),
      1
    )[0]
  );
  const atemporalObj = variables.reduce(
    (acc, v) => ({
      ...acc,
      [`${v.title}${v.unit ? ` [${v.unit}]` : ""}`]: v.default,
    }),
    {}
  );
  const array = [atemporalObj];
  const opts = {
    header: Object.keys(atemporalObj),
  };
  const worksheet = utils.json_to_sheet(array, opts);
  utils.book_append_sheet(workbook, worksheet, "Generators Atemporal Variables");

  // Temporal variables
  const yearsArray = Array(numYears)
    .fill(0)
    .map((_v, i) => `${startYear + i}`);
  const tempHeaders = ["Name", "Attribute", ...yearsArray];
  const tempVariables = Object.values(generatorGenericSchema).filter(
    (value) => value.yearly && value.title && value.items.type === "number"
  );
  const tempArray = tempVariables.map((v) => ({
    Name: atemporalObj.Name,
    Attribute: v.title,
    ...yearsArray.reduce((acc, year) => ({ ...acc, [`${year}`]: v.items.default }), {}),
  }));
  const tempWorksheet = utils.json_to_sheet(tempArray, { header: tempHeaders });
  utils.book_append_sheet(workbook, tempWorksheet, "Generators Temporal Variables");
  return workbook;
};

const parseStorages = () => {};

export { parseGenerators, generatorsTemplate, parseStorages };
